import { useEffect, useImperativeHandle } from "react";

import { Editor } from "@tiptap/react";

import { DocumentEditorRef, useEditorRefStore } from "@demo/store/editor-ref";
import { Role } from "@demo/types/base";

/**
 * Hook to handle editor registration with the store and ref forwarding.
 *
 * @param editor The editor instance
 * @param editorRole The role of the editor
 * @param ref The forwarded ref
 * @param commands Command functions for the editor
 */
export function useEditorRegistration(
  editor: Editor | null,
  editorRole: Role,
  ref: React.ForwardedRef<DocumentEditorRef>,
  commands: {
    focus: () => void;
    clear: () => void;
    getEditor: () => Editor | null;
    handleFileDrop: (files: File[]) => void;
  },
) {
  const setEditorRef = useEditorRefStore((state) => state.setEditorRef);

  // Setup imperative handle
  useImperativeHandle(
    ref,
    () => ({
      editor,
      focus: commands.focus,
      clear: commands.clear,
      getEditor: commands.getEditor,
      handleFileDrop: commands.handleFileDrop,
    }),
    [editor, commands],
  );

  // Register editor in store
  useEffect(() => {
    if (ref && "current" in ref && editor) {
      setEditorRef(editorRole, {
        editor,
        focus: commands.focus,
        clear: commands.clear,
        getEditor: commands.getEditor,
        handleFileDrop: commands.handleFileDrop,
      });
    }

    return () => {
      if (ref && "current" in ref) {
        setEditorRef(editorRole, null);
      }
    };
  }, [editor, editorRole, ref, setEditorRef, commands]);
}
